import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { SchoolMemberService } from '../../../../services/member.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { DlgSelectOptionComponent } from '../../../components/dialogs/dlg-select-option/dlg-select-option.component';

@Component({
  selector: 'app-dlg-link-parent',
  standalone: true,
  imports: [ReactiveFormsModule, InitialsPipe],
  templateUrl: './dlg-link-parent.component.html',
  styleUrl: './dlg-link-parent.component.css',
})
export class DlgLinkParentComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<DlgSelectOptionComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private dialog: MatDialog = inject(MatDialog);
  toastr: ToastrService = inject(ToastrService);
  memberService: SchoolMemberService = inject(SchoolMemberService);

  searchForm: FormGroup;
  isSearching: boolean = false;

  store: Store<AppState> = inject(Store<AppState>);

  title: string = 'Link Parent';
  description: string = 'Enter the parents name';
  action: string = 'Link';

  parentsFound: SchoolMember[] | undefined = undefined;

  selectedParent: SchoolMember | null = null;

  group: Group | null | undefined = undefined;
  student: SchoolMember | null;
  grades: Grade[] | null | undefined = [];

  working$: Observable<boolean> = of(false);

  exit$: Subject<void> = new Subject<void>();
  utilsService = UtilsService;

  constructor(private actions$: Actions) {
    this.searchForm = new FormGroup({
      searchInput: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50),
      ]),
    });

    this.student = this.data.student ?? null;

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });
  }

  ngOnInit(): void {
    if (this.student == null) {
      this.toastr.error('No student selected', 'Error Occurred');
      this.dialogRef.close(false);
    }

    this.actions$
      .pipe(ofType(memberActions.linkParentSuccess))
      .pipe(takeUntil(this.exit$))
      .subscribe(() => {
        this.dialogRef.close(true);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  async searchParents() {
    // console.log(
    //   `search parents :: ${this.searchForm.get('searchInput')?.value}`
    // );
    if (this.searchForm.get('searchInput')?.errors) return;
    this.searchForm.setErrors(null);
    this.parentsFound = undefined;
    this.selectedParent = null;
    this.isSearching = true;
    try {
      const result = await this.memberService.searchMembers(
        this.searchForm.get('searchInput')?.value,
        this.group?.id!,
        { memberType: [SchoolMemberType.parent] },
      );
      // console.debug(`Parents Found: ${result?.length}`);
      // console.log(JSON.stringify(result, null));
      this.parentsFound = result;
      // console.log('parents found', this.parentsFound);
    } catch (error) {
      // console.log(
      //   'DlgLinkParentComponent :: Error searching parents',
      //   JSON.stringify(error, null, 2)
      // );
      this.parentsFound = [];
      this.searchForm.setErrors({ searchFailed: true });
    }
    this.isSearching = false;
  }

  async submit(): Promise<void> {
    try {
      // this.toastr.success(result.message, 'Success');
      this.working$ = of(true);

      if (this.selectedParent == null) {
        this.toastr.error('Please select a parent to link', 'Error Occurred');
        return;
      }

      if (
        this.selectedParent.studentId == null ||
        this.selectedParent.studentId?.findIndex(
          (id) => id === this.student?.memberId,
        ) === -1
      ) {
        this.selectedParent.studentId = this.selectedParent.studentId ?? [];
        this.selectedParent.studentId.push(this.student?.memberId!);
      } else {
        this.searchForm.setErrors({ exists: true });
        return;
      }

      let student: SchoolMember = {
        id: this.student!.id,
        groupId: this.student!.groupId,
        academicYear: this.group?.academicYear,
        memberId: this.student!.memberId,
        parentId: this.selectedParent.memberId,
        parentName: this.selectedParent.name,
        modified: Timestamp.now(),
      };
      // console.log(`Selected Student: ${JSON.stringify(this.student, null, 2)}`);

      let parent: SchoolMember = {
        id: this.selectedParent.id,
        groupId: this.selectedParent.groupId,
        academicYear: this.group?.academicYear,
        memberId: this.selectedParent.memberId,
        studentId: this.selectedParent.studentId,
        modified: Timestamp.now(),
      };

      this.store.dispatch(
        memberActions.linkParent({
          student: student,
          parent: parent,
        }),
      );
      // console.log(`submit : Action dispatched`);
    } catch (error) {
      // console.log(`DlgLinkParentComponent :: submit : Error: `, error);
      this.working$ = of(false);
      this.toastr.error('Failed to link Parent', 'Error Occurred');
    }
  }

  onChangeOption(item: SchoolMember): void {
    if (this.selectedParent == null || this.selectedParent?.id != item?.id) {
      this.selectedParent = item;
    } else {
      this.selectedParent = null;
    }
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title,
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
